/* Presentation only: retain original media, full names and all shopping controls. */
(() => {
  const imageSelector = '.wcn-category-tile__media img, .wcn-shop-card__image img, .wcn-routine-card__media img, .product-card-wrapper .card__media img';

  // Only uniform white/transparent outer margins qualify. Uncertain images stay contained.
  function contentBounds(data, width, height) {
    const blank = (x, y) => {
      const i = (y * width + x) * 4;
      const alpha = data[i + 3] / 255;
      return [0, 1, 2].every(c => 255 - (255 - data[i + c]) * alpha >= 250);
    };
    if (width < 16 || height < 16) return null;
    for (let x = 0; x < width; x++) if (!blank(x, 0) || !blank(x, height - 1)) return null;
    for (let y = 0; y < height; y++) if (!blank(0, y) || !blank(width - 1, y)) return null;
    let left = width, top = height, right = -1, bottom = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (blank(x, y)) continue;
        left = Math.min(left, x); right = Math.max(right, x);
        top = Math.min(top, y); bottom = Math.max(bottom, y);
      }
    }
    if (right < left || bottom < top) return null;
    // Retain a conservative halo around faint edges, packaging and shadows.
    const pad = Math.max(3, Math.ceil(Math.max(width, height) * 0.025));
    left = Math.max(0, left - pad); top = Math.max(0, top - pad);
    right = Math.min(width, right + pad + 1); bottom = Math.min(height, bottom + pad + 1);
    if ((right - left) * (bottom - top) / (width * height) < 0.10) return null;
    return { left: left / width, top: top / height, right: right / width, bottom: bottom / height };
  }

  function fitBounds(bounds, ratio) {
    if (!bounds || !Number.isFinite(ratio) || ratio <= 0) return null;
    const containWidth = Math.min(1, ratio), containHeight = Math.min(1, 1 / ratio);
    const width = (bounds.right - bounds.left) * containWidth;
    const height = (bounds.bottom - bounds.top) * containHeight;
    const scale = Math.min(2.4, 0.92 / Math.max(width, height));
    return {
      scale,
      x: (0.5 - (bounds.left + bounds.right) / 2) * containWidth * scale * 100,
      y: (0.5 - (bounds.top + bounds.bottom) / 2) * containHeight * scale * 100
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { contentBounds, fitBounds };
  if (typeof document === 'undefined') return;

  const measured = new WeakMap();
  function fitImage(img) {
    if (!img.complete || !img.naturalWidth || measured.get(img) === img.currentSrc) return;
    measured.set(img, img.currentSrc);
    ['--wcn-image-scale', '--wcn-image-x', '--wcn-image-y'].forEach(key => img.style.removeProperty(key));
    img.dataset.wcnImageFit = 'contained';
    try {
      const ratio = img.naturalWidth / img.naturalHeight;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(16, Math.round(192 * Math.min(1, ratio)));
      canvas.height = Math.max(16, Math.round(192 * Math.min(1, 1 / ratio)));
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;
      // Uses the already loaded image. No fetch, new image request, upload or storage.
      context.drawImage(img, 0, 0, canvas.width, canvas.height);
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      const bounds = contentBounds(pixels.data, canvas.width, canvas.height);
      const fit = fitBounds(bounds, ratio);
      if (!fit) return;
      img.style.setProperty('--wcn-image-scale', fit.scale.toFixed(4));
      img.style.setProperty('--wcn-image-x', `${fit.x.toFixed(4)}%`);
      img.style.setProperty('--wcn-image-y', `${fit.y.toFixed(4)}%`);
      img.dataset.wcnImageFit = 'normalized';
    } catch (_) {
      // Cross-origin restrictions or unusual formats keep the full uncropped image.
      img.dataset.wcnImageFit = 'contained';
    }
  }

  const fields = [
    ':scope > h3, .card > .card__content > .card__information > .card__heading',
    ':scope > .wcn-shop-card__vendor, .card > .card__content > .card__information .caption-with-letter-spacing',
    ':scope > p',
    '.card > .card__content > .card__information .price',
    '.card > .card__content > .card__information'
  ];
  function alignGrid(grid) {
    const cards = [...grid.querySelectorAll('.product-card-wrapper, .wcn-shop-card, .wcn-routine-card')]
      .filter(card => card.getClientRects().length && !card.closest('.quick-add-modal'));
    cards.forEach(card => fields.forEach(selector => card.querySelector(selector)?.style.removeProperty('min-height')));
    const rows = [];
    cards.forEach(card => {
      const top = card.getBoundingClientRect().top;
      let row = rows.find(item => Math.abs(item.top - top) < 2);
      if (!row) { row = { top, cards: [] }; rows.push(row); }
      row.cards.push(card);
    });
    rows.forEach(row => {
      if (row.cards.length < 2) return;
      fields.forEach(selector => {
        const elements = row.cards.map(card => card.querySelector(selector)).filter(Boolean);
        const height = Math.ceil(Math.max(0, ...elements.map(el => el.getBoundingClientRect().height)));
        elements.forEach(el => { el.style.minHeight = `${height}px`; });
      });
    });
  }

  const observed = new WeakSet();
  let scheduled = false;
  const refresh = () => {
    scheduled = false;
    document.querySelectorAll(imageSelector).forEach(img => {
      if (!observed.has(img)) {
        observed.add(img);
        img.addEventListener('load', () => fitImage(img));
      }
      fitImage(img);
    });
    document.querySelectorAll('.product-grid, .wcn-next-products, .wcn-routine-section').forEach(alignGrid);
  };
  const schedule = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(refresh); } };
  const start = () => {
    schedule();
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('pageshow', schedule);
    document.fonts?.ready.then(schedule);
    document.addEventListener('shopify:section:load', schedule);
    const main = document.getElementById('MainContent');
    if (main) new MutationObserver(schedule).observe(main, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
